import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { Editor, ReadOnlyEditor } from 'src/components/Editor';
import { useMe } from 'src/hooks';
import useRealtimeEditorConfig from 'src/hooks/doc/useRealtimeEditorConfig';
import { setReleaseNoteEditor } from 'src/reactives/releaseNote.reactive';
import { ActionId } from 'src/services/editor/editorActions';
import { INCOGNITO_NAME } from 'src/utils/users.util';

import { ReleaseNoteSkeleton } from './ReleaseNote.styles';

export const ReleaseNoteEditor = ({
  releaseNoteId, isReadOnly, isEnabled,
}: {
  releaseNoteId: string;
  isReadOnly?: boolean;
  isEnabled?: boolean;
}) => {
  const theme = useTheme();
  const { me } = useMe();
  const cursorName = me.incognito ? INCOGNITO_NAME : `${me.firstName} ${me.lastName}`;

  const prevNoteId = useRef<string | null>();
  const [isEditorInit, setIsEditorInit] = useState(false);

  const {
    provider,
    yDoc,
    isSync,
    isOffline,
  } = useRealtimeEditorConfig({
    docId: releaseNoteId,
    isEnabled,
    isEditorInit,
  });

  useEffect(() => {
    if (isSync && !isEditorInit) {
      prevNoteId.current = releaseNoteId;
      setIsEditorInit(true);
    } else if (isEditorInit && releaseNoteId !== prevNoteId.current) {
      prevNoteId.current = releaseNoteId;
      setIsEditorInit(false);
    }
  }, [isEditorInit, isSync, releaseNoteId]);

  if (!isEditorInit) return <ReleaseNoteSkeleton />;

  if (isReadOnly) {
    return (
      <ReadOnlyEditor
        style={{ padding: 24 }}
        interactive
        collaboration={yDoc ? { document: yDoc } : undefined}
        onEditorReady={editor => {
          setReleaseNoteEditor({ editor });
        }}
      />
    );
  }

  return (
    <Editor
      // TODO: replace the placeholder when AI is ready
      // emptyPlaceholder="Type '/' for commands, start writing your release note, or get help from Cycle AI below"
      emptyPlaceholder="Type '/' for commands, start writing your release note"
      hideQuickActions
      disabledActions={[
        ActionId.MentionDoc,
        ActionId.MentionUser,
        ActionId.GithubIssue,
        ActionId.Linear,
        ActionId.Notion,
        ActionId.TurnTextIntoDocMention,
        ActionId.TurnTextIntoDocMentionLastUsed,
        ActionId.TurnTextIntoGithubIssueMention,
        ActionId.TurnTextIntoInsight,
        ActionId.TurnTextIntoLinearMention,
        ActionId.Comments,
      ]}
      showAddTemplate={false}
      smallEmptyBlock
      isReadOnly={isReadOnly || isOffline}
      collaboration={yDoc ? { document: yDoc } : undefined}
      cursors={provider ? {
        provider,
        user: {
          color: theme.userColors.main,
          name: cursorName,
        },
      } : undefined}
      onEditorReady={editor => {
        setReleaseNoteEditor({ editor });
      }}
    />
  );
};
